import { FC, useState } from "react";
import {
    Badge,
    Box,
    Button,
    Divider,
    FormControl,
    FormLabel,
    Grid,
    GridItem,
    Heading,
    HStack,
    Icon,
    IconButton,
    Input,
    Skeleton,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
    VStack,
} from "@chakra-ui/react";
import { IconEdit } from "@tabler/icons-react";

import { useCandidateWages } from "../../api/hooks/useCandidateWages";
import { EntityCandidateForUpdate } from "../../api/models/EntityCandidate";
import { useCandidatePatch } from "../../api/hooks/useCandidatePatch";
import { buildJsonPatch } from "../../utils/buildJsonPatch";
import { CandidateWage } from "../../api/models/CandidateWage";
import { formatDateStr } from "@am-tax/fe-core";
import { useUserContext } from "../../context";

interface Props {
    cId: string | undefined;
    eId: string | undefined;
    candidateId: string | undefined;
    candidate: Partial<EntityCandidateForUpdate>;
    canEdit: boolean;
}

type WageFormValues = {
    wagesHourlyRate: number | undefined;
    wagesTotalHours: number | undefined;
    wagesAnnualSalary: number | undefined;
    wagesTotalPay: number | undefined;
};

function formatCurrency(value: number | undefined): string {
    if (value == null) return "—";
    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatWageNumber(value: number | undefined): string {
    if (value == null) return "—";
    return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const WageRecordRow: FC<{ wage: CandidateWage; dateFormat: string }> = ({ wage, dateFormat }) => (
    <Tr>
        <Td>{formatDateStr(wage.asOfDate, dateFormat) ?? "—"}</Td>
        <Td isNumeric>{formatWageNumber(wage.hourlyRate)}</Td>
        <Td isNumeric>{formatWageNumber(wage.totalHours)}</Td>
        <Td isNumeric>{formatCurrency(wage.annualSalary)}</Td>
        <Td isNumeric>{formatCurrency(wage.totalPay)}</Td>
        <Td>
            {wage.isImported ? (
                <Badge colorScheme="blue" variant="subtle">
                    Payroll Import
                </Badge>
            ) : (
                <Badge colorScheme="gray" variant="subtle">
                    Manual
                </Badge>
            )}
        </Td>
    </Tr>
);

export const CandidateWageHistorySection: FC<Props> = ({ cId, eId, candidateId, candidate, canEdit }) => {
    const { dateFormat } = useUserContext();
    const wagesQuery = useCandidateWages(cId, eId, candidateId);
    const patchCandidate = useCandidatePatch();

    const [showForm, setShowForm] = useState(false);
    const [formValues, setFormValues] = useState<WageFormValues>({
        wagesHourlyRate: candidate.wagesHourlyRate,
        wagesTotalHours: candidate.wagesTotalHours,
        wagesAnnualSalary: candidate.wagesAnnualSalary,
        wagesTotalPay: candidate.wagesTotalPay,
    });

    const wages = wagesQuery.data ?? [];
    const [latest, ...history] = wages;

    const handleOpenForm = () => {
        setFormValues({
            wagesHourlyRate: candidate.wagesHourlyRate,
            wagesTotalHours: candidate.wagesTotalHours,
            wagesAnnualSalary: candidate.wagesAnnualSalary,
            wagesTotalPay: candidate.wagesTotalPay,
        });
        setShowForm(true);
    };

    const parseNumber = (value: string): number | undefined => (value === "" ? undefined : Number(value));

    const handleSave = async () => {
        const originalValues: Record<string, unknown> = {
            wagesHourlyRate: candidate.wagesHourlyRate,
            wagesTotalHours: candidate.wagesTotalHours,
            wagesAnnualSalary: candidate.wagesAnnualSalary,
            wagesTotalPay: candidate.wagesTotalPay,
        };
        const dirtyFields: Record<string, unknown> = {};
        const newValues: Record<string, unknown> = { ...formValues };

        for (const key of Object.keys(formValues) as Array<keyof WageFormValues>) {
            if (formValues[key] !== originalValues[key]) {
                dirtyFields[key] = true;
            }
        }

        const ops = buildJsonPatch(dirtyFields, newValues);
        if (ops.length > 0) {
            await patchCandidate.mutateAsync(
                { clientId: cId, entityId: eId, candidateId, patch: ops },
                {
                    onSuccess: () => {
                        wagesQuery.refetch();
                        setShowForm(false);
                    },
                }
            );
        } else {
            setShowForm(false);
        }
    };

    return (
        <>
            <HStack justify={"space-between"} mb={".75rem"}>
                <Heading variant={"info"}>Pay / Wage</Heading>
                {canEdit && !showForm && (
                    <Tooltip label="Update wages">
                        <IconButton size={"sm"} icon={<Icon as={IconEdit} />} aria-label={"Update Wages"} onClick={handleOpenForm} />
                    </Tooltip>
                )}
            </HStack>

            {wagesQuery.isLoading ? (
                <VStack align="stretch" gap=".5rem">
                    <Skeleton height="20px" />
                    <Skeleton height="20px" />
                </VStack>
            ) : wages.length === 0 ? (
                <Text color="gray.400" fontStyle="italic">
                    No wage history recorded.
                </Text>
            ) : (
                <>
                    {/* Latest record — shown prominently */}
                    <Box bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.100" p="1rem" mb=".75rem">
                        <HStack justify="space-between" mb=".5rem">
                            <Text fontWeight="semibold" fontSize="sm" color="blue.700">
                                Current
                            </Text>
                            <HStack spacing=".5rem">
                                <Text fontSize="xs" color="gray.500">
                                    {formatDateStr(latest.asOfDate, dateFormat) ?? "—"}
                                </Text>
                                {latest.isImported ? (
                                    <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                                        Payroll Import
                                    </Badge>
                                ) : (
                                    <Badge colorScheme="gray" variant="subtle" fontSize="xs">
                                        Manual
                                    </Badge>
                                )}
                            </HStack>
                        </HStack>
                        <Grid gridTemplateColumns={"auto 1fr auto 1fr"} columnGap={"1rem"} rowGap={".25rem"} alignItems={"center"}>
                            <GridItem>
                                <Text variant="label">Hourly Rate:</Text>
                            </GridItem>
                            <GridItem>{formatWageNumber(latest.hourlyRate)}</GridItem>
                            <GridItem>
                                <Text variant="label">Total Hours:</Text>
                            </GridItem>
                            <GridItem>{formatWageNumber(latest.totalHours)}</GridItem>
                            <GridItem>
                                <Text variant="label">Annual Salary:</Text>
                            </GridItem>
                            <GridItem>{formatCurrency(latest.annualSalary)}</GridItem>
                            <GridItem>
                                <Text variant="label">Total Pay:</Text>
                            </GridItem>
                            <GridItem>{formatCurrency(latest.totalPay)}</GridItem>
                        </Grid>
                    </Box>
                </>
            )}

            {/* Inline update form */}
            {showForm && (
                <Box bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.200" p="1rem" mb=".75rem">
                    <Text fontWeight="semibold" fontSize="sm" mb=".75rem" color="gray.700">
                        Update Wages
                    </Text>
                    <VStack gap=".75rem" alignItems="stretch">
                        <HStack gap="1rem" width="100%" alignItems="stretch">
                            <FormControl>
                                <FormLabel fontSize="sm">Hourly Rate:</FormLabel>
                                <Input
                                    type="number"
                                    step=".01"
                                    size="sm"
                                    value={formValues.wagesHourlyRate ?? ""}
                                    onChange={e => setFormValues(v => ({ ...v, wagesHourlyRate: parseNumber(e.target.value) }))}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Total Hours:</FormLabel>
                                <Input
                                    type="number"
                                    step=".01"
                                    size="sm"
                                    value={formValues.wagesTotalHours ?? ""}
                                    onChange={e => setFormValues(v => ({ ...v, wagesTotalHours: parseNumber(e.target.value) }))}
                                />
                            </FormControl>
                        </HStack>
                        <HStack gap="1rem" width="100%" alignItems="stretch">
                            <FormControl>
                                <FormLabel fontSize="sm">Annual Salary:</FormLabel>
                                <Input
                                    type="number"
                                    step=".01"
                                    size="sm"
                                    value={formValues.wagesAnnualSalary ?? ""}
                                    onChange={e => setFormValues(v => ({ ...v, wagesAnnualSalary: parseNumber(e.target.value) }))}
                                />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Total Pay:</FormLabel>
                                <Input
                                    type="number"
                                    step=".01"
                                    size="sm"
                                    value={formValues.wagesTotalPay ?? ""}
                                    onChange={e => setFormValues(v => ({ ...v, wagesTotalPay: parseNumber(e.target.value) }))}
                                />
                            </FormControl>
                        </HStack>
                        <HStack justify="flex-end" pt=".25rem">
                            <Button variant="primary" size="sm" isLoading={patchCandidate.isLoading} loadingText="Saving" onClick={handleSave}>
                                Save
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setShowForm(false)} isDisabled={patchCandidate.isLoading}>
                                Cancel
                            </Button>
                        </HStack>
                    </VStack>
                </Box>
            )}

            {/* Historical records table */}
            {history.length > 0 && (
                <>
                    <Divider mb=".5rem" />
                    <Text fontSize="sm" color="gray.500" mb=".5rem">
                        Previous Records
                    </Text>
                    <Box overflowX="auto">
                        <Table size="sm" variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>As Of</Th>
                                    <Th isNumeric>Hourly Rate</Th>
                                    <Th isNumeric>Total Hours</Th>
                                    <Th isNumeric>Annual Salary</Th>
                                    <Th isNumeric>Total Pay</Th>
                                    <Th>Source</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {history.map(wage => (
                                    <WageRecordRow key={wage.id} wage={wage} dateFormat={dateFormat} />
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                </>
            )}
        </>
    );
};
